import { provide, inject, type InjectionKey } from 'vue';

interface DataBusComposable<DataType> {
    (): DataType;
    (providedData: DataType): void;
}

/**
 * Creates a composable for sharing data between components using Vue's provide/inject mechanism.
 * 
 * @template T - The type of data to be shared
 * @returns A composable function that can provide data when called with an argument, 
 *          or inject data when called without arguments
 * 
 * @example
 * ```typescript
 * // Create the composable
 * const useSharedCounter = createDataBusComposable<{ count: number }>();
 * 
 * // In parent component - provide data
 * useSharedCounter({ count: 0 });
 * 
 * // In child component - inject data
 * const counter = useSharedCounter();
 * ```
 */
const createDataBusComposable = <T>(): DataBusComposable<T> => {
    const injectionKey = Symbol() as InjectionKey<T>;

    const dataBus = (providedData?: T): T | void => {
        if (providedData === undefined) {
            const injectedData = inject(injectionKey);
            
            if (injectedData === undefined) {
                throw new Error('Data bus composable: No data was provided. Make sure to call the composable with data in a parent component.');
            }
            
            return injectedData;
        }
        
        provide(injectionKey, providedData);
    };

    return dataBus as DataBusComposable<T>;
};

export {
    type DataBusComposable,
    createDataBusComposable
};
